import { IonButton, IonCol, IonContent, IonGrid, IonRow } from '@ionic/react';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import styles from '@components/FullscreenModal.module.css';
import HugeText from '@components/text/HugeText';
import Icon from '@components/text/Icon';
import LargeText from '@components/text/LargeText';
import useFullscreenModal from '@hooks/useFullscreenModal';
import { Opener } from '@hooks/useOpener';
import { imageUrl } from '@utils/imageUtils';
import { clickCurrentTarget, handleTrapFocus } from '@utils/keyboardUtils';

type ChildrenData = {
  modalDisplayed: boolean;
};

type Props = {
  children: (data: ChildrenData) => ReactNode;
  dataCy?: string;
  hideImage?: boolean;
  name: string;
  onCancel?: () => void;
  opener: Opener;
  title?: string;
};

type ModalBodyProps = {
  children: (data: ChildrenData) => ReactNode;
  dataCy?: string;
  hideImage?: boolean;
  modalDisplayed: boolean;
  opener: Opener;
  title?: string;
};

const ModalBody = ({ children, dataCy, hideImage, modalDisplayed, opener, title }: ModalBodyProps) => {
  const intl = useIntl();

  const fullScreenModalRef = useRef<HTMLDivElement>(null);

  return (
    <IonContent
      onKeyDown={event => {
        if (opener.isOpen) {
          handleTrapFocus(event, fullScreenModalRef, opener.close);
        }
      }}
    >
      <div data-test={dataCy} ref={fullScreenModalRef}>
        <div className={styles.background}>
          <div>
            <IonGrid>
              <IonRow className="ion-align-items-center">
                <IonCol>
                  <img
                    alt={intl.formatMessage({ id: 'accessibility.altTags.purpleLogo' })}
                    className={styles.logo}
                    src={imageUrl('logoPurple')}
                  />
                </IonCol>
                <IonCol className={styles.title} size="auto">
                  <HugeText>{title}</HugeText>
                </IonCol>
                <IonCol className={styles.end}>
                  <IonButton
                    className={styles.closeButton}
                    data-test="close-modal"
                    onClick={opener.close}
                    onKeyUp={clickCurrentTarget}
                    tabIndex={0}
                  >
                    <Icon ariaLabelKey="accessibility.ariaLabels.close" icon="times" />
                  </IonButton>
                </IonCol>
              </IonRow>
              <IonRow>
                <IonCol className={styles.titleMobile}>
                  <LargeText>{title}</LargeText>
                </IonCol>
              </IonRow>
            </IonGrid>
            {children({ modalDisplayed })}
          </div>

          {hideImage ? (
            <div className="ion-margin-bottom" />
          ) : (
            <div className={styles.bottomImage}>
              <img
                alt={intl.formatMessage({ id: 'accessibility.altTags.activeElders' })}
                className={styles.bottomImage}
                src={imageUrl('activities')}
              />
            </div>
          )}
        </div>
      </div>
    </IonContent>
  );
};

const FullscreenModal = ({ children, hideImage = false, name, onCancel, opener, title }: Props) => {
  const [modalDisplayed, setModalDisplayed] = useState<boolean>(false);

  const handleDidDismiss = useCallback(() => {
    if (onCancel) {
      onCancel();
    }
    opener.close();
    setModalDisplayed(false);
  }, [onCancel, opener]);

  const handleDidPresent = useCallback(() => {
    setModalDisplayed(true);
  }, []);

  const handleWillDismiss = useCallback(() => {
    setModalDisplayed(false);
  }, []);

  const modal = useMemo(
    () => (
      <ModalBody hideImage={hideImage} modalDisplayed={modalDisplayed} opener={opener} title={title}>
        {children}
      </ModalBody>
    ),
    [children, hideImage, modalDisplayed, opener, title]
  );

  const { dismiss: dismissModal, present: presentModal } = useFullscreenModal(name);

  useEffect(() => {
    if (opener.isOpen) {
      presentModal({
        content: modal,
        onDidDismiss: handleDidDismiss,
        onDidPresent: handleDidPresent,
        onWillDismiss: handleWillDismiss
      });
    }
  }, [dismissModal, handleDidDismiss, handleDidPresent, handleWillDismiss, modal, opener.isOpen, presentModal]);

  useEffect(() => {
    if (!opener.isOpen) {
      dismissModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opener.isOpen]);

  useEffect(
    () => () => {
      dismissModal();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return <></>;
};

export default FullscreenModal;
